/* ==========================================================================
   VRTX CORE — scroll reveals, counters, marquee duplication
   Zero dependencies. Respects prefers-reduced-motion.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveals(root) {
    var els = (root || document).querySelectorAll('[data-vx-reveal], .vx-lines');
    if (!els.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Animated counters: <span data-vx-count="3500">0</span> ---------- */
  function initCounters(root) {
    var els = (root || document).querySelectorAll('[data-vx-count]');
    if (!els.length) return;

    function run(el) {
      var target = parseFloat(el.getAttribute('data-vx-count')) || 0;
      var suffix = el.getAttribute('data-vx-suffix') || '';
      if (reduced) { el.textContent = target.toLocaleString() + suffix; return; }
      var dur = 1400;
      var start = null;
      function step(ts) {
        if (!start) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(target * eased).toLocaleString() + suffix;
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    if (!('IntersectionObserver' in window)) { els.forEach(run); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { run(entry.target); io.unobserve(entry.target); }
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Marquee: duplicate content until track fills 2x viewport ---------- */
  function initMarquees(root) {
    (root || document).querySelectorAll('[data-vx-marquee]').forEach(function (track) {
      if (track.dataset.vxReady) return;
      track.dataset.vxReady = '1';
      var content = track.innerHTML;
      var safety = 0;
      /* fill to at least one viewport... */
      while (track.scrollWidth < window.innerWidth && safety < 12) {
        track.innerHTML += content;
        safety++;
      }
      /* ...then mirror into two identical halves so translateX(-50%) loops
         seamlessly regardless of content width (no jump on repeat). */
      track.innerHTML += track.innerHTML;
      if (reduced) track.style.animation = 'none';
    });
  }

  function boot() {
    initReveals();
    initCounters();
    initMarquees();
  }

  /* ---------- Discount code: tell them in the CART, not silently at checkout ----------
     The theme's coupon panel promises "Coupon code will work on checkout page" and just
     stashes the code in a hidden field. On any cart carrying bundle pricing that promise
     is false — Shopify drops the code at checkout with no error and no trace of it.
     cart.js's `applicable` flag matches the checkout outcome exactly (verified against a
     real checkout), so we can give a straight answer here instead. -------------------- */

  var VX_MONEY = function (cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB',
        { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP' })
        .format(cents / 100);
    } catch (e) { return '£' + (cents / 100).toFixed(2); }
  };

  function vxCodeBox(near) {
    var box = document.querySelector('[data-vx-codemsg]');
    if (box) return box;
    /* cart page has no container of its own — put one next to the field */
    if (!near) return null;
    var host = near.closest('.t4s-field') || near.parentElement;
    if (!host) return null;
    box = document.createElement('div');
    box.className = 'vx-codemsg';
    box.setAttribute('data-vx-codemsg', '');
    box.hidden = true;
    host.parentElement.insertBefore(box, host.nextSibling);
    return box;
  }

  function vxShowCode(box, tone, headline, detail) {
    if (!box) return;
    box.className = 'vx-codemsg is-' + tone;
    box.innerHTML = '<strong>' + headline + '</strong>' + (detail ? '<span>' + detail + '</span>' : '');
    box.hidden = false;
  }

  function vxCheckCode(code, box) {
    /* redirect to cart.js so the discount route returns a few hundred bytes, not a page */
    fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', { cache: 'no-store' })
      .then(function () { return fetch('/cart.js', { cache: 'no-store' }); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var hit = (cart.discount_codes || []).filter(function (d) {
          return String(d.code).toLowerCase() === code.toLowerCase();
        })[0];
        if (!hit) return;
        /* "bundle pricing is on" = a line discount that did NOT come from a code.
           total_discount alone is not enough (it counts a code applied earlier), and
           our promo-engine Function reports type "" rather than "automatic" — so test
           for "not a discount_code" instead of naming the type. */
        var autoTotal = 0;
        (cart.items || []).forEach(function (item) {
          (item.line_level_discount_allocations || []).forEach(function (alloc) {
            var app = alloc.discount_application || {};
            if (app.type !== 'discount_code') autoTotal += alloc.amount || 0;
          });
        });

        if (hit.applicable) {
          vxShowCode(box, 'ok', code.toUpperCase() + ' applied.', 'It will show on the next step.');
        } else if (autoTotal > 0) {
          /* Lead with the saving, not the refusal. cart.js reports an unknown code and a
             non-combinable one identically, so we assert no cause — just the figure. */
          vxShowCode(box, 'info', 'You’ve saved ' + VX_MONEY(autoTotal) + ' with the bundle price.',
            code.toUpperCase() + ' hasn’t been added on top of it.');
        } else {
          vxShowCode(box, 'warn', code.toUpperCase() + ' can’t be used on this order.',
            'Double-check the code, or contact us and we’ll sort it.');
        }
      })
      .catch(function () { /* never block the cart on this */ });
  }

  /* Delegated: the drawer is re-rendered by the Section Rendering API, so we can't
     bind to the button itself. We do NOT preventDefault — the theme still saves the
     code for checkout exactly as before; we only add the explanation. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-action="save-discountcode"]');
    if (!btn) return;
    var scope = btn.closest('.t4s-mini_cart-tool__content') || document;
    var input = scope.querySelector('#CartDiscountcode, input[data-name="discount"]')
             || document.querySelector('#CartDiscountcode, input[data-name="discount"]');
    var code = input && input.value ? input.value.trim() : '';
    if (!code) return;
    var box = vxCodeBox(input);
    setTimeout(function () { vxCheckCode(code, box); }, 250);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-init inside Shopify theme editor when sections load/reorder */
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
    initCounters(e.target);
    initMarquees(e.target);
  });

  /* Expose for section-level scripts */
  window.VRTX = { initReveals: initReveals, initCounters: initCounters, initMarquees: initMarquees };
})();
